import {API_CONFIG} from '../config/api.config';
import {handleUnauthorizedError} from '../services/authErrorHandler';

export interface ApiResponse<T> {
  data: T;
  status: number;
  statusText: string;
}

export interface ApiError {
  message: string;
  status?: number;
  data?: any;
}

// Shared refresh state across all ApiClient instances
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// Set during app init: reads refresh_token, calls /api/auth/refresh, saves new token, returns new access_token
let globalRefreshCallback: (() => Promise<string | null>) | null = null;
// Set during app init: called when refresh fails (forces logout)
let globalLogoutCallback: (() => Promise<void>) | null = null;

export function setGlobalRefreshCallback(cb: () => Promise<string | null>): void {
  globalRefreshCallback = cb;
}

export function setGlobalLogoutCallback(cb: () => Promise<void>): void {
  globalLogoutCallback = cb;
}

export class ApiClient {
  private baseURL: string;
  private timeout: number;

  constructor(baseURL: string = API_CONFIG.baseURL, timeout: number = API_CONFIG.timeout) {
    this.baseURL = baseURL;
    this.timeout = timeout;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry = false,
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const mergedHeaders = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
      };

      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: mergedHeaders,
      });

      clearTimeout(timeoutId);

      let data: T;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = (await response.text()) as unknown as T;
      }

      if (!response.ok) {
        if (response.status === 401 && !isRetry && globalRefreshCallback) {
          // Deduplicate concurrent refresh calls
          if (!isRefreshing) {
            isRefreshing = true;
            refreshPromise = globalRefreshCallback().finally(() => {
              isRefreshing = false;
              refreshPromise = null;
            });
          }

          const newToken = await refreshPromise;

          if (newToken) {
            // Retry with new token
            const retryHeaders = {
              ...(options.headers as Record<string, string>),
              Authorization: `Bearer ${newToken}`,
            };
            return this.request<T>(endpoint, {...options, headers: retryHeaders}, true);
          }

          // Refresh failed — logout
          setTimeout(async () => {
            if (globalLogoutCallback) await globalLogoutCallback();
            else await handleUnauthorizedError();
          }, 0);
        } else if (response.status === 401 && isRetry) {
          // Still 401 after refresh — logout
          setTimeout(async () => {
            if (globalLogoutCallback) await globalLogoutCallback();
            else await handleUnauthorizedError();
          }, 0);
        }

        const error: ApiError = {
          message:
            (data as any)?.msg ||
            (data as any)?.message ||
            `HTTP error! status: ${response.status}`,
          status: response.status,
          data,
        };
        throw error;
      }

      return {
        data,
        status: response.status,
        statusText: response.statusText,
      };
    } catch (error: any) {
      clearTimeout(timeoutId);

      if (error.name === 'AbortError') {
        throw {message: 'Request timeout', status: 408} as ApiError;
      }

      if (error.status) {
        throw error;
      }

      throw {message: error.message || 'Network error', status: 0} as ApiError;
    }
  }

  async get<T>(endpoint: string, headers?: HeadersInit): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {method: 'GET', headers});
  }

  async post<T>(endpoint: string, body?: any, headers?: HeadersInit): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      headers,
    });
  }

  async put<T>(endpoint: string, body?: any, headers?: HeadersInit): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
      headers,
    });
  }

  async delete<T>(endpoint: string, headers?: HeadersInit): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {method: 'DELETE', headers});
  }

  async patch<T>(endpoint: string, body?: any, headers?: HeadersInit): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
      headers,
    });
  }

  setAuthToken(_token: string | null): void {}
}

// Export singleton instances
export const apiClient = new ApiClient();
export const seliApiClient = new ApiClient(API_CONFIG.seliBaseURL, API_CONFIG.timeout);
export const sociantApiClient = new ApiClient(API_CONFIG.sociantBaseURL, API_CONFIG.timeout);
